import DealFilters from '@/components/all-from-router/_components/deal-filters/deal-filters.tsx';
import {
  FilteredDealsProvider,
  useFilteredDealsContext,
} from '@/components/all-from-router/_components/deal-filters/filtered-deals/filtered-deals-provider.tsx';
import { DealScope } from '@/components/all-from-router/_components/deal-filters/filtered-deals/use-filtered-deals.ts';
import {
  FILTER_TYPES,
  type FilterType,
} from '@/components/all-from-router/_components/deal-filters/types.ts';
import { Button } from '@/components/ui/button.tsx';
import ArchivedDealsList from '@/components/ui/deal-list/archived-deals-list.tsx';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import { Kbd } from '@/components/ui/kbd';
import HeaderWithSidebarToggle from '@/components/ui/layout/header-with-sidebar-toggle.tsx';
import useAllListAndBoardHotkeys from '@/components/ux/all-list-and-board/use-all-list-and-board-hotkeys.ts';
import {
  type DealFilter,
  type FilterCondition,
  useArchivedDeals,
} from '@/hooks/fetch-store-and-stitch/use-archived-deals.ts';
import {
  FEATURE_FLAG_NAMES,
  useIsFeatureEnabled,
} from '@/hooks/use-feature-flags.ts';
import getOS from '@/lib/get-operating-system';
import { HOTKEYS } from '@/lib/hotkeys';
import type { ValueOf } from '@/lib/utils.ts';
import { dealStore } from '@/models/stores';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { debounce } from 'lodash';
import { SearchIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

export const Route = createFileRoute('/(authenticated)/$hub/archived-deals/')({
  component: ArchivedDealsPageWrapper,
});

const FILTER_PREFIXES = {
  BEFORE: 'before',
  AFTER: 'after',
  IS: 'is',
  IS_NOT: 'is_not',
} as const;
type FILTER_PREFIXES = ValueOf<typeof FILTER_PREFIXES>;

const PAST_FILTER_VALUE = 'in-the-past';

const simpleFilterTypes: Set<FilterType> = new Set(FILTER_TYPES);

const dateFilterTypes: Set<FilterType> = new Set([
  'delivery_date',
  'created_date',
  'pickup_date',
  'last_updated_date',
]);

const getDateFilterPrefix = (
  isNegated: boolean,
  useBeforeAfter: boolean,
): FILTER_PREFIXES => {
  if (useBeforeAfter) {
    return isNegated ? FILTER_PREFIXES.AFTER : FILTER_PREFIXES.BEFORE;
  }
  return isNegated ? FILTER_PREFIXES.IS_NOT : FILTER_PREFIXES.IS;
};

const getSimpleFilterPrefix = (isNegated: boolean): FILTER_PREFIXES => {
  return isNegated ? FILTER_PREFIXES.IS_NOT : FILTER_PREFIXES.IS;
};

const ArchivedDealsPage = observer(() => {
  const { enableScope } = useAllListAndBoardHotkeys();
  const { filters, matchPreference } = useFilteredDealsContext();
  const [findInViewQuery, setFindInViewQuery] = useState('');
  const [_focusSearchInput, setFocusSearchInput] = useState(false);
  const queryClient = useQueryClient();
  const searchInputRef = useRef<HTMLInputElement>(
    null,
  ) as React.RefObject<HTMLInputElement>;

  useHotkeys(HOTKEYS.FIND_IN_VEHICLES, (e) => {
    e.preventDefault();
    searchInputRef.current?.focus();
    setFocusSearchInput(true);
  });

  const os = getOS();
  const modifierKey = useMemo(() => {
    return os === 'mac' ? '⌘' : 'Ctrl';
  }, [os]);

  const debouncedSetQuery = useMemo(
    () => debounce((value: string) => setFindInViewQuery(value), 300),
    [],
  );

  useEffect(() => {
    debouncedSetQuery(findInViewQuery);
    return () => debouncedSetQuery.cancel();
  }, [findInViewQuery, debouncedSetQuery]);

  const dealFilter = useMemo((): DealFilter | undefined => {
    const conditions: FilterCondition[] = [];

    filters.forEach((filter) => {
      if (filter.filterMetaState.isCurrentlyBeingBuilt) return;

      if (dateFilterTypes.has(filter.filterMetaState.type)) {
        const useBeforeAfter = filter.selectedFilters !== PAST_FILTER_VALUE;
        const prefix = getDateFilterPrefix(
          filter.filterMetaState.isNegated,
          useBeforeAfter,
        );
        conditions.push({
          [filter.filterMetaState.type]: {
            equals: `${prefix}-${filter.selectedFilters}`,
          },
        });
        return;
      }

      if (simpleFilterTypes.has(filter.filterMetaState.type)) {
        const prefix = getSimpleFilterPrefix(filter.filterMetaState.isNegated);
        const selectedValues = Object.entries(filter.selectedFilters)
          .filter(([_, selected]) => selected)
          .map(([value]) => `${prefix}-${value}`);

        if (selectedValues.length > 0) {
          conditions.push({
            [filter.filterMetaState.type]: { in: selectedValues },
          });
        }
      }
    });

    // DECOMMISSION: archived deals are shown across ALL hubs, so no
    // hubPathIdentifier scoping is applied here.
    if (conditions.length === 0 && !findInViewQuery.trim()) return;

    const andConditions: FilterCondition[] = [];

    if (findInViewQuery.trim()) {
      andConditions.push({
        searchText: { containsIgnoreCase: findInViewQuery.trim() },
      });
    }

    if (conditions.length > 0) {
      if (matchPreference === 'match-all-filters') {
        andConditions.push(...conditions);
      } else {
        andConditions.push({ or: conditions });
      }
    }
    return { and: andConditions };
  }, [filters, findInViewQuery, matchPreference]);

  const datasetKey = useMemo(() => {
    const filterHash = dealFilter ? JSON.stringify(dealFilter) : '';
    return `all|${filterHash}`;
  }, [dealFilter]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error,
  } = useArchivedDeals({
    limit: 50,
    dealFilter: dealFilter,
  });

  const seenPageCountRef = useRef(0);
  const loadingRef = useRef(false);
  const prevDatasetKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (prevDatasetKeyRef.current === datasetKey) return;
    dealStore.clearAllArchivedDeals();
    seenPageCountRef.current = 0;

    queryClient.resetQueries({ queryKey: ['archivedDeals'], exact: true });

    prevDatasetKeyRef.current = datasetKey;
  }, [datasetKey, queryClient]);

  useEffect(() => {
    const pages = data?.pages ?? [];
    if (pages.length > seenPageCountRef.current) {
      const newlyArrived = pages.slice(seenPageCountRef.current);
      for (const page of newlyArrived) {
        dealStore.ingestArchivedDealPage(page.items, {});
      }
      seenPageCountRef.current = pages.length;
    }
  }, [data?.pages]);

  const archivedDeals = dealStore.getAllArchivedDealList();

  useEffect(() => {
    dealStore.populateFilteredArchivedDeals(archivedDeals);
  }, [archivedDeals]);

  useEffect(() => {
    return () => {
      seenPageCountRef.current = 0;
      prevDatasetKeyRef.current = null;
    };
  }, []);

  const handleEndReached = useCallback(async () => {
    if (!hasNextPage || isFetchingNextPage || loadingRef.current) return;
    loadingRef.current = true;
    try {
      await fetchNextPage();
    } finally {
      loadingRef.current = false;
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const noPagesYet = !data || (data.pages?.length ?? 0) === 0;
  const showPanel = status === 'pending' && noPagesYet;

  return (
    <div className="flex flex-col h-screen min-h-0 overflow-hidden">
      <div className="flex flex-row w-full gap-6 p-2">
        <HeaderWithSidebarToggle>
          <InputGroup className="max-w-xs">
            <InputGroupInput
              ref={searchInputRef}
              placeholder="Search archived deals..."
              value={findInViewQuery}
              onChange={(e) => setFindInViewQuery(e.target.value)}
              onFocus={() => setFocusSearchInput(true)}
              onBlur={() => setFocusSearchInput(false)}
            />
            <InputGroupAddon>
              <SearchIcon />
            </InputGroupAddon>
            <InputGroupAddon align="inline-end">
              <Kbd>{modifierKey}</Kbd>
              <Kbd>F</Kbd>
            </InputGroupAddon>
          </InputGroup>
          <DealFilters />
        </HeaderWithSidebarToggle>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden relative">
        {showPanel ? (
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <div className="w-full max-w-md rounded-lg border bg-white p-6 text-center">
              <div className="text-gray-500">Loading archived deals...</div>
            </div>
          </div>
        ) : status === 'error' && noPagesYet ? (
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <div className="w-full max-w-md rounded-lg border bg-white p-6 text-center">
              <div className="text-red-500">
                Error loading archived deals
                {error?.message ? `: ${error.message}` : ''}
              </div>
            </div>
          </div>
        ) : (
          <>
            <ArchivedDealsList
              dealsData={archivedDeals}
              filterCallback={() => true}
              enableScope={enableScope}
              findInViewFilterCallback={() => true}
              onEndReached={handleEndReached}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              prefetchOffset={8}
            />
            <div className="flex flex-col items-center gap-2 py-4">
              {isFetchingNextPage && (
                <div className="text-sm text-gray-400">Loading more...</div>
              )}
              {!hasNextPage && (
                <div className="text-xs text-gray-300">No more results</div>
              )}
              {hasNextPage && !isFetchingNextPage && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fetchNextPage()}
                >
                  Load more
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
});

function ArchivedDealsPageWrapper() {
  const { isFeatureEnabled: isArchiveEnabled } = useIsFeatureEnabled({
    featureFlagName: FEATURE_FLAG_NAMES.ARCHIVE_DEAL,
  });

  if (!isArchiveEnabled) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="w-full max-w-md rounded-lg border bg-white p-6 text-center">
          <div className="text-gray-500">
            You don’t have access to Archived Deals.
          </div>
        </div>
      </div>
    );
  }

  return (
    <FilteredDealsProvider scope={DealScope.Archived}>
      <ArchivedDealsPage />
    </FilteredDealsProvider>
  );
}
